import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import {
  GetCustomerPreferences,
  IPreferences,
  PostCustomerPreferences,
  PutCustomerPreferences,
} from "api/rpc/2024-04/customer/customer";

import Page from "components/page/Page";
import PreferenceCard from "components/preferenceCard/PreferenceCard";

import "./portalPreferences.scss";
import axios, { CancelToken } from "axios";
import Callout from "components/callout/Callout";
import Spin from "components/spin/spin";
import ReactDOM from "react-dom";
import { isEqualWith } from "lodash";
import { isEmpty } from "helpers/Helpers";
import { ButtonNew as Button } from "components/buttonNew";
import { GetClient, IClient } from "api/rpc/2024-04/customer/client";
import { GetCustomerLeague, PutLeagueParticipant } from "api/rpc/2024-04/customer/league";
import { useGuestPortalContext } from "./GuestPortalContext";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Toggle from "components/form/toggle/Toggle";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import Panel from "components/panel/Panel";
import DataTable from "components/dataTable/DataTable";

interface IPreferencesState {
  preferences: Array<IPreferences>;
  clients: Array<IClient>;
}

interface ILeagueState {
  leagues: Array<TLeague>;
  leaguePreferencesVisible: boolean;
  clientId: number;
  toggleAll: boolean;
}

type TLeague = {
  id: number;
  name: string;
  league_participant_display_name_on_tee_sheet: number;
  league_participant_id: number;
  facility_long_name: string;
};

export default function PortalPreferences() {
  const dispatch = useAppDispatch();
  const { portalState } = useGuestPortalContext();
  const windowSize = useWindowSize();
  const { t } = useTranslation();

  const [preferencesState, setPreferencesState] = useState<IPreferencesState>({
    preferences: null,
    clients: null,
  });

  const [leagueState, setLeagueState] = useState<ILeagueState>({
    leagues: undefined,
    leaguePreferencesVisible: false,
    clientId: null,
    toggleAll: false,
  });

  const [preferencesStateBeforeChanges, setPreferencesStateBeforeChanges] = useState<Array<IPreferences>>(null);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadPreferences(source.token);
    return () => {
      source.cancel("Cancelled");
    };
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (leagueState.leaguePreferencesVisible && leagueState.clientId) {
      void loadLeagues(source.token);
    }
    return () => source.cancel();
  }, [leagueState?.leaguePreferencesVisible]);

  async function loadPreferences(cancelToken: CancelToken = null) {
    const [prefRes, clientRes] = await Promise.all([
      GetCustomerPreferences(null, false, cancelToken),
      GetClient({ collect: true }, false),
    ]);

    if (prefRes.status !== StatusCode.OK || clientRes?.status !== StatusCode.OK) {
      if (prefRes.message === "Cancelled") {
        return;
      }
      ReactDOM.unstable_batchedUpdates(() => {
        setPreferencesState(prevState => ({
          ...prevState,
          preferences: [],
        }));
        setPreferencesStateBeforeChanges([]);
      });
      dispatch(showError(t("Error loading preferences")));
      return;
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setPreferencesState(prevState => ({
        ...prevState,
        preferences: prefRes?.data,
        clients: clientRes?.data,
      }));
      setPreferencesStateBeforeChanges(prefRes?.data);
    });
  }

  async function loadLeagues(cancelToken: CancelToken) {
    //display loader
    if (leagueState.leagues !== undefined) {
      setLeagueState(prevState => ({ ...prevState, leagues: undefined }));
    }
    const leagueRes = await GetCustomerLeague(
      {
        year: new Date().getFullYear(),
        user_id: portalState?.guest?.id,
        client_id: leagueState.clientId,
        extended: true,
      },
      false,
      cancelToken,
    );

    if (leagueRes?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken.reason) {
        return;
      }
      setLeagueState(prevState => ({ ...prevState, leagues: [] }));
      dispatch(showError(t("Error loading leagues")));
      return;
    }

    const leagues = leagueRes?.data as unknown as Array<TLeague>;
    //Sort leagues by facility
    const sortedLeagues = leagues?.sort((a, b) => a.facility_long_name.localeCompare(b.facility_long_name));

    setLeagueState(prevState => ({
      ...prevState,
      leagues: sortedLeagues,
      toggleAll: sortedLeagues?.every(league => !!league?.league_participant_display_name_on_tee_sheet),
    }));
  }

  async function savePreferences() {
    const preferences = [...preferencesState.preferences];
    dispatch(enqueue());
    for (const [index, preference] of preferences.entries()) {
      if (!isEqualWith(preference, preferencesStateBeforeChanges[index])) {
        const prefRes = await PutCustomerPreferences(
          {
            client_id: preference.client_id,
            ...preference,
          },
          false,
        );

        if (prefRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error updating preferences")));
          dispatch(dequeue());
          return;
        }
      }
    }
    setPreferencesStateBeforeChanges(preferencesState?.preferences);
    dispatch(dequeue());
    dispatch(showSuccess(t("Preferences updated sucessfully")));
  }

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>, preferenceIndex: number) {
    const { id, checked } = event.target;
    const updatedPreferences = [...preferencesState.preferences];
    updatedPreferences[preferenceIndex] = { ...updatedPreferences[preferenceIndex], [id]: checked };
    setPreferencesState(prevState => ({ ...prevState, preferences: updatedPreferences }));
  }

  function cancelUnsavedChanges() {
    setPreferencesState(prevState => ({ ...prevState, preferences: preferencesStateBeforeChanges }));
  }

  function unsavedChangesExist() {
    if (preferencesStateBeforeChanges === null) {
      return false;
    }

    return !isEqualWith(preferencesState?.preferences, preferencesStateBeforeChanges, (originalValue, newValue) => {
      if (isEmpty(originalValue) && isEmpty(newValue)) {
        return true;
      }
    });
  }

  async function createPreferences(clientId: number) {
    const prefRes = await PostCustomerPreferences({ client_id: clientId }, true);
    if (prefRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating preferences")));
      return;
    }
    ReactDOM.unstable_batchedUpdates(() => {
      setPreferencesState(prevState => ({ ...prevState, preferences: null, clients: null }));
      setPreferencesStateBeforeChanges(null);
    });
    dispatch(showSuccess(t("Successfully created preferences")));
    await loadPreferences();
  }

  function updateParticipantToggle(e: React.ChangeEvent<HTMLInputElement>, leagueId: number) {
    const { id, checked } = e.target;
    const updatedLeagues = [...leagueState.leagues]?.map(league =>
      league.id === leagueId ? { ...league, [id]: Number(checked) } : league,
    );
    setLeagueState(prevState => ({
      ...prevState,
      leagues: updatedLeagues,
      toggleAll: updatedLeagues?.every(league => !!league.league_participant_display_name_on_tee_sheet),
    }));
  }

  function handleToggleAllChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    const updatedLeagues = [...leagueState.leagues]?.map(league => ({ ...league, [id]: Number(checked) }));
    setLeagueState(prevState => ({ ...prevState, leagues: updatedLeagues, toggleAll: checked }));
  }

  async function saveLeagueParticipantPreferences() {
    const participantData = leagueState?.leagues?.map(league => ({
      id: league.league_participant_id,
      display_name_on_tee_sheet: league.league_participant_display_name_on_tee_sheet,
    }));
    const res = await PutLeagueParticipant({ participants: participantData }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating league preferences")));
      return;
    }
    dispatch(showSuccess(t("League preferences saved successfully")));
    void closeLeaguePreferences();
  }

  function closeLeaguePreferences() {
    setLeagueState(prevState => ({
      ...prevState,
      leagues: undefined,
      leaguePreferencesVisible: false,
      clientId: null,
      toggleAll: false,
    }));
  }

  const primaryAction = {
    content: t("Save Preferences"),
    action: savePreferences,
    disabled: !unsavedChangesExist(),
  };

  return (
    <Page
      title={t("Preferences")}
      primaryAction={primaryAction}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: savePreferences,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <div>
        <div>
          <span className="portal-preferences-header">Notifications Settings</span>
          <p className="text-subdued">
            {
              t(
                "We may still send you important notifications about your account outside of your normal notification settings",
              )
            }
          </p>
        </div>
        {preferencesState?.clients ? (
          preferencesState?.clients?.length > 0 ? (
            preferencesState?.clients?.map((client, index) => {
              const preference = preferencesState?.preferences?.find(preference => preference?.client_id === client.id);
              return (
                <div key={index} className="portal-preferences-container">
                  <div className="portal-preferences-client-container">
                    {client?.logo_source_url ? (
                      <img
                        className="portal-preferences-client-logo"
                        src={client?.logo_source_url}
                        alt={`${client?.long_name} Logo`}
                      />
                    ) : (
                      <p className="portal-preferences-client-name">{client?.long_name}</p>
                    )}
                    <Button
                      size="medium"
                      type="secondary"
                      onClick={() =>
                        setLeagueState(prevState => ({
                          ...prevState,
                          leaguePreferencesVisible: true,
                          clientId: client?.id,
                        }))
                      }
                    >
                      League Preferences
                    </Button>
                  </div>
                  {preference ? (
                    <>
                      <div className="portal-preferences-divider" />

                      <PreferenceCard
                        title={t("Booking Confirmations")}
                        description={t("Notifications when you create a new booking")}
                        handleToggleChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggleChange(e, index)}
                        options={[
                          {
                            label: t("Email"),
                            id: "booking_confirmation_email",
                            checked: preference.booking_confirmation_email,
                          },
                        ]}
                      />
                      {/* <PreferenceCard
                          title={t("Booking Updates")}
                          description={t("Notifications when you update an upcoming booking")}
                          handleToggleChange={handleToggleChange}
                          options={[
                            {
                              label: t("Email"),
                              id: "booking_update_email",
                              checked: state.preferences?.booking_update_email,
                              disabled: true,
                            },
                          ]}
                        /> */}
                      <PreferenceCard
                        title={t("Booking Cancellations")}
                        description={t("Notifications when you cancel an upcoming booking")}
                        handleToggleChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggleChange(e, index)}
                        options={[
                          {
                            label: t("Email"),
                            id: "booking_cancellation_email",
                            checked: preference.booking_cancellation_email,
                          },
                        ]}
                      />

                      <PreferenceCard
                        title={t("Display Name on Tee Sheet")}
                        description={t(
                          "Display your name on tee sheet on online booking portal. Other users will be able to see your name when booking tee times on the tee sheet",
                        )}
                        handleToggleChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggleChange(e, index)}
                        options={[
                          {
                            label: t("Display"),
                            id: "display_name_on_tee_sheet",
                            checked: preference.display_name_on_tee_sheet,
                          },
                        ]}
                      />
                    </>
                  ) : (
                    <Button size="medium" type="text" onClick={() => createPreferences(client.id)}>
                      {t("Add Preferences")}
                    </Button>
                  )}
                </div>
              );
            })
          ) : (
            <div>
              <br />
              <Callout
                type="info"
                title={t("No Preferences found")}
                content={t("There are no preference settings for this account")}
              />{" "}
            </div>
          )
        ) : (
          <span style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <Spin />
          </span>
        )}
      </div>

      <Portal isMounted={windowSize.width > MOBILE_WIDTH && leagueState.leaguePreferencesVisible}>
        <Sheet
          size="medium"
          open={windowSize.width > MOBILE_WIDTH && leagueState?.leaguePreferencesVisible}
          closable
          title={t("League Preferences")}
          cancelText={t("Close")}
          onOk={saveLeagueParticipantPreferences}
          okText={t("Save")}
          okDisabled={!leagueState?.leagues || leagueState?.leagues?.length === 0}
          onCancel={closeLeaguePreferences}
        >
          {leagueState?.leagues === undefined || leagueState?.leagues?.length > 0 ? (
            <DataTable
              columns={[
                { label: t("Facility") },
                { label: t("League") },
                {
                  label: t("Display Name On Tee Sheet"),
                  content: (
                    <div className="portal-preferences-league-container">
                      <Toggle
                        id="league_participant_display_name_on_tee_sheet"
                        onChange={e => handleToggleAllChange(e)}
                        checked={leagueState.toggleAll}
                      />
                      <span className="portal-preferences-league-text">
                        Display Name on Tee Sheet{" "}
                        <OverlayTrigger
                          placement="auto"
                          overlay={
                            <Tooltip id="leaguePreferencesToolTip">
                              When enabled, your name will be visible to other users on the selected league&apos;s tee
                              sheet
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon
                            style={{ fontSize: "14px", marginLeft: "5px" }}
                            icon={["far", "info-square"]}
                          />
                        </OverlayTrigger>
                      </span>
                    </div>
                  ),
                },
              ]}
              loading={leagueState?.leagues === undefined}
            >
              {leagueState?.leagues?.map((league, index) => {
                return (
                  <tr key={index}>
                    <td>{league.facility_long_name}</td>
                    <td>{league?.name}</td>
                    <td>
                      <Toggle
                        id="league_participant_display_name_on_tee_sheet"
                        onChange={e => updateParticipantToggle(e, league.id)}
                        checked={!!league?.league_participant_display_name_on_tee_sheet}
                      />
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          ) : (
            <Callout
              title={t("No Leagues Found")}
              content={t("No league participant information found for this season")}
              type="info"
            />
          )}
        </Sheet>
      </Portal>

      <Panel
        open={windowSize.width <= MOBILE_WIDTH && leagueState.leaguePreferencesVisible}
        onClose={closeLeaguePreferences}
        title={t("League Preferences")}
        animate
        primaryAction={{
          onClick: () => saveLeagueParticipantPreferences(),
          label: t("Save"),
          disabled: !leagueState?.leagues || leagueState?.leagues?.length === 0,
        }}
        secondaryAction={{
          onClick: () => closeLeaguePreferences(),
          label: t("Close"),
        }}
      >
        {leagueState?.leagues === undefined ? (
          <div style={{ height: "32px" }}>
            <Spin />
          </div>
        ) : leagueState?.leagues?.length > 0 ? (
          <div>
            <div className="portal-preferences-league-header-container">
              <div className="portal-preferences-league-cards portal-preferences-league-header">
                <p className="portal-preferences-league-text">League</p>
                <p className="portal-preferences-league-text">
                  Display Name on Tee Sheet{" "}
                  <OverlayTrigger
                    placement="auto"
                    overlay={
                      <Tooltip id="leaguePreferencesToolTip">
                        When enabled, your name will be visible to other users on the selected league&apos;s tee sheet
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon style={{ fontSize: "14px", marginLeft: "5px" }} icon={["far", "info-square"]} />
                  </OverlayTrigger>
                </p>
              </div>
            </div>
            {leagueState?.leagues?.map((league, index) => (
              <div key={index} className="portal-preferences-league-cards">
                <div className="portal-preferences-league-cards-facility">
                  <p className="portal-preferences-facility">{league.facility_long_name}</p>
                  <p className="portal-preferences-league">{league.name}</p>
                </div>
                <div>
                  <Toggle
                    id="league_participant_display_name_on_tee_sheet"
                    checked={!!league?.league_participant_display_name_on_tee_sheet}
                    onChange={e => updateParticipantToggle(e, league.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Callout
            title={t("No Leagues Found")}
            content={t("No league participant information found for this season")}
            type="info"
          />
        )}
      </Panel>
    </Page>
  );
}
